import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { useLocation, Link, useNavigate, useParams } from 'react-router-dom';
import siteService from '../../services/siteService';
import { Search, Plus, Edit3, ChevronDown, ChevronUp, Users, UserMinus, UserPlus, Filter, Building } from 'lucide-react';
import EmptyState from '../ui/EmptyState';
import PageLoader from '../ui/PageLoader';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';
import permissionsService from '../../services/permissionsService';

const Sites = ({ studyID: studyIDProp }) => {
  // The study page passes the study; the /studies/:studyID/sites route only has it in the address.
  const params = useParams();
  const studyID = studyIDProp || params.studyID;
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'site_number', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const { isAuthenticated, user } = useContext(AuthContext);
  const [filters, setFilters] = useState({
    activeParticipants: [],
    deactivatedParticipants: [],
    totalParticipants: []
  });
  const [permissions, setPermissions] = useState({
    canAddSite: false,
    canChangeSite: false
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const sitesData = await siteService.getSitesForStudy(studyID);
      setSites(sitesData);
    } catch (error) {
      console.error('Error fetching data: ', error);
      setError('An error occurred while fetching the data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [studyID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasAddSite, hasChangeSite] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_SITE)
          ]);
          setPermissions({
            canAddSite: hasAddSite,
            canChangeSite: hasChangeSite
          });
          console.log('[SITES] Permissions set:', { canAddSite: hasAddSite, canChangeSite: hasChangeSite });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setPermissions({
            canAddSite: false,
            canChangeSite: false
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  const extractSiteNumber = (siteName) => {
    const match = siteName.match(/Site (\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedSites = useMemo(() => {
    let sortableSites = [...sites];
    if (sortConfig.key) {
      sortableSites.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'site_number') {
          aValue = extractSiteNumber(a.site_name);
          bValue = extractSiteNumber(b.site_name);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSites;
  }, [sites, sortConfig]);

  const filteredSites = useMemo(() => {
    return sortedSites.filter(site =>
      site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filters.activeParticipants.length === 0 || filters.activeParticipants.includes(site.active_participants_count.toString())) &&
      (filters.deactivatedParticipants.length === 0 || filters.deactivatedParticipants.includes(site.deactivated_participants_count.toString())) &&
      (filters.totalParticipants.length === 0 || filters.totalParticipants.includes(site.total_participants_count.toString()))
    );
  }, [sortedSites, searchTerm, filters]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSites.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const filterOptions = {
      activeParticipants: [...new Set(sites.map(site => site.active_participants_count.toString()))],
      deactivatedParticipants: [...new Set(sites.map(site => site.deactivated_participants_count.toString()))],
      totalParticipants: [...new Set(sites.map(site => site.total_participants_count.toString()))]
    };

    return (
      <div className="mb-4 space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        {Object.entries(filterOptions).map(([filterType, options]) => (
          <div key={filterType}>
            <h3 className="mb-2 text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">{filterType.replace(/([A-Z])/g, ' $1').trim()}</h3>
            <div className="flex flex-wrap gap-2">
              {options.map(option => (
                <button
                  key={option}
                  onClick={() => toggleFilter(filterType, option)}
                  aria-pressed={filters[filterType].includes(option)}
                  className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition-colors ${filters[filterType].includes(option)
                      ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500'
                    }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          { key: 'site_number', label: 'Site Name' },
          { key: 'active_participants_count', label: 'Active' },
          { key: 'deactivated_participants_count', label: 'Deactivated' },
          { key: 'total_participants_count', label: 'Total' },
          { key: 'actions', label: 'Actions' }
        ].map(({ key, label }) => (
          <th
            key={key}
            className="th-cell cursor-pointer whitespace-nowrap"
            onClick={() => requestSort(key)}
          >
            <div className="flex items-center gap-1">
              {label}
              {sortConfig.key === key && (
                sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableBody = () => (
    <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
      {currentItems.length === 0 && (
        <tr>
          <td colSpan={5}>
            <EmptyState icon={Building} title="No sites found" />
          </td>
        </tr>
      )}
      {currentItems.map((site) => (
        <tr key={site.id} className="tr-interactive">
          <td className="td-cell whitespace-nowrap">
            <Link to={`/studies/${studyID}/sites/${site.external_id}`} className="link">{site.site_name}</Link>
          </td>
          <td className="td-cell whitespace-nowrap">
            <div className="flex items-center gap-2">
              <UserPlus size={14} className="text-success-600 dark:text-success-400" />
              <span className="tabular-nums text-gray-900 dark:text-gray-100">{site.active_participants_count}</span>
            </div>
          </td>
          <td className="td-cell whitespace-nowrap">
            <div className="flex items-center gap-2">
              <UserMinus size={14} className="text-danger-600 dark:text-danger-400" />
              <span className="tabular-nums text-gray-900 dark:text-gray-100">{site.deactivated_participants_count}</span>
            </div>
          </td>
          <td className="td-cell whitespace-nowrap">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-primary-600 dark:text-primary-400" />
              <span className="tabular-nums text-gray-900 dark:text-gray-100">{site.total_participants_count}</span>
            </div>
          </td>
          <td className="td-cell whitespace-nowrap">
          {permissions.canChangeSite && (
            <Link
              to={`/studies/${studyID}/sites/${site.external_id}/edit`}
              className="inline-flex rounded-lg p-1.5 text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10"
            >
              <Edit3 size={16} />
            </Link>
          )}
          </td>
        </tr>
      ))}
    </tbody>
  );

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
          <Users className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">Study Sites</h2>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
          <input
            type="text"
            placeholder="Search sites..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${showFilters ? 'border border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-400/30 dark:bg-primary-400/10 dark:text-primary-300' : 'btn-secondary'}`}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {permissions.canAddSite && (
          <button
            onClick={() => navigate(`/studies/${studyID}/add-site`)}
            className="btn btn-primary"
          >
            <Plus size={16} />
            Add New Site
          </button>
          )}
        </div>
      </div>

      {showFilters && renderFilterOptions()}

      {isLoading ? (
        <PageLoader className="h-64" />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {renderTableHeader()}
                {renderTableBody()}
              </table>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{filteredSites.length === 0 ? 0 : indexOfFirstItem + 1}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(indexOfLastItem, filteredSites.length)}</span> of{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{filteredSites.length}</span> results
              </p>
            </div>
            <nav className="inline-flex items-center gap-1" aria-label="Pagination">
              {Array.from({ length: Math.ceil(filteredSites.length / itemsPerPage) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  aria-current={currentPage === index + 1 ? 'page' : undefined}
                  className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors ${currentPage === index + 1
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06]'
                    }`}
                >
                  {index + 1}
                </button>
              ))}
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default Sites;